import type {Content} from '@prismicio/client'
import {asText, isFilled} from '@prismicio/client'
import type {SliceComponentProps} from '@prismicio/react'
import {useRouter} from 'next/router'
import {useLinkResolver} from 'prismicio'
import {getLinkLocale} from 'src/common/helpers/prismicLink'
import {useTranslate} from 'src/common/i18n/useTranslate'
import type {IconType} from 'src/common/Icon'
import {iconNames} from 'src/common/Icon'
import type {Context} from 'src/common/types'
import {HandoffBlock} from './HandoffBlock'
import {PrerequisitesPanel} from './PrerequisitesPanel'
import {TrustBlock} from './TrustBlock'
import {ValueHero} from './ValueHero'

export type DiscoveryBlockProps = SliceComponentProps<Content.DiscoveryBlockSlice> & {context: Context}

const MAX_PREREQUISITE_NEEDS = 5

const toIconType = (value: string): IconType => {
  const match = iconNames.find((iconName) => iconName === value)
  return match ?? 'sparkle'
}

const DiscoveryBlock = ({slice, context: {pageUid}}: DiscoveryBlockProps): JSX.Element | null => {
  const {linkResolver} = useLinkResolver()
  const {locale} = useRouter()
  const translate = useTranslate()

  switch (slice.variation) {
    case 'valueHero':
      return (
        <ValueHero
          heading={isFilled.keyText(slice.primary.heading) ? slice.primary.heading : ''}
          image={slice.primary.image}
          annotation={isFilled.keyText(slice.primary.annotation) ? slice.primary.annotation : ''}
          limitsNote={isFilled.keyText(slice.primary.limits_note) ? slice.primary.limits_note : ''}
          statCards={slice.primary.stat_cards.map((statCard) => ({
            icon: isFilled.select(statCard.icon) ? toIconType(statCard.icon) : 'sparkle',
            value: isFilled.keyText(statCard.value) ? statCard.value : '',
            detail: isFilled.keyText(statCard.detail) ? statCard.detail : '',
          }))}
        />
      )
    case 'prerequisitesPanel':
      return (
        <PrerequisitesPanel
          heading={isFilled.keyText(slice.primary.heading) ? slice.primary.heading : ''}
          needs={slice.primary.needs.slice(0, MAX_PREREQUISITE_NEEDS).map((need) => ({
            label: isFilled.keyText(need.label) ? need.label : '',
            detail: isFilled.keyText(need.detail) ? need.detail : '',
            buttonLabel: isFilled.keyText(need.button_label) ? need.button_label : '',
            buttonHref: isFilled.link(need.button_link)
              ? linkResolver(need.button_link, 'prerequisites-need')
              : undefined,
            buttonTarget:
              isFilled.link(need.button_link) && need.button_link.link_type === 'Web'
                ? need.button_link.target
                : undefined,
            buttonLocale: getLinkLocale(need.button_link, locale),
          }))}
          timeEstimate={isFilled.keyText(slice.primary.time_estimate) ? slice.primary.time_estimate : ''}
          effortLevel={
            isFilled.select(slice.primary.effort_level)
              ? translate(`discoveryBlock.effortLevels.${slice.primary.effort_level}`)
              : ''
          }
          endResult={isFilled.keyText(slice.primary.end_result) ? slice.primary.end_result : ''}
          pageUid={pageUid}
        />
      )
    case 'handoffBlock':
      return (
        <HandoffBlock
          heading={isFilled.keyText(slice.primary.heading) ? slice.primary.heading : ''}
          selfIdPrompt={isFilled.keyText(slice.primary.self_id_prompt) ? slice.primary.self_id_prompt : ''}
          trustSummary={isFilled.keyText(slice.primary.trust_summary) ? slice.primary.trust_summary : ''}
          trustLinkAnchor="trust"
          routes={slice.items.map((item) => ({
            routeLabel: isFilled.keyText(item.route_label) ? item.route_label : '',
            routeDescription: isFilled.keyText(item.route_description) ? item.route_description : '',
            isAvailable: item.is_available ?? false,
            isRecommended: item.is_recommended ?? false,
            promptText: isFilled.richText(item.prompt_or_message)
              ? (asText(item.prompt_or_message, {separator: '\n'}) ?? '')
              : '',
            failureNote: isFilled.keyText(item.failure_note) ? item.failure_note : '',
          }))}
          pageUid={pageUid}
        />
      )
    case 'trustBlock':
      return (
        <TrustBlock
          heading={isFilled.keyText(slice.primary.heading) ? slice.primary.heading : ''}
          dataDestination={isFilled.keyText(slice.primary.data_destination) ? slice.primary.data_destination : ''}
          readerResponsibility={
            isFilled.keyText(slice.primary.reader_responsibility) ? slice.primary.reader_responsibility : ''
          }
          accessPoints={slice.primary.access_points.map((accessPoint) => ({
            label: isFilled.keyText(accessPoint.label) ? accessPoint.label : '',
            detail: isFilled.keyText(accessPoint.detail) ? accessPoint.detail : '',
          }))}
          flowSteps={slice.primary.flow_steps.map((flowStep) => ({
            icon: isFilled.select(flowStep.icon) ? toIconType(flowStep.icon) : 'sparkle',
            label: isFilled.keyText(flowStep.label) ? flowStep.label : '',
          }))}
        />
      )
    default:
      return null
  }
}

export default DiscoveryBlock
